import jsurl from 'jsurl';

import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {encodeQueryParams} from 'common/report/util/url';
import {DEFAULT_USER_NAME} from 'common/report/util/profile/types';
import {MPProperties} from 'common/constants';
import {getUserName} from 'common/util/profiles';
import {UserSummaryPropertyVariables} from 'common/widgets/profile-user-summary/types';
import {
  FormattedProperty,
  getNameProperties,
  getRawPropertyValue,
  getRenderInputPropertyValue,
  ValidPropertyValue,
} from 'common/util/format-property';
import {generatePermalink} from 'common/report/util/insights-permalink';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';

export const VALID_TIME_FORMAT = `YYYY-MM-DDTHH:mm:ss`;

export function getUserPropertiesFromApiResult(result): {[name: string]: ValidPropertyValue} {
  return result?.results?.[0]?.$properties || {};
}

export function checkProfileExists(result): boolean {
  return result?.results?.length > 0 || false;
}

export function extractHashParams(
  hashParams: string,
): {
  distinctId: string;
  dataGroupId: string;
  jsUrl: string;
} {
  const params = {
    distinctId: null,
    dataGroupId: null,
    jsUrl: null,
  };
  // distinct_id could include `&`. the hashParams passed in is get directly from window.location.hash.slice(1)
  // so when `distinctId` we need to decode it
  hashParams.split(`&`).forEach((param) => {
    const splitParam = splitOnFirstMatch(`=`, param);
    switch (splitParam[0]) {
      case `distinct_id`:
        params[`distinctId`] = decodeURIComponent(splitParam[1]);
        break;
      case `data_group_id`:
        params[`dataGroupId`] = splitParam[1];
        break;
      default:
        // the JSURL rest, not as a key
        params[`jsUrl`] = splitParam[0];
        break;
    }
  });

  return params;
}

/**
 * Split data string into two parts, separated by, but not including, separator
 * @param separator
 * @param data
 *
 * @example splitOnFirstMatch(`=`, `distinct_id=1234`) => [`distinct_id`, `1234`]
 */
export function splitOnFirstMatch(separator: string, data: string): [string, string] {
  for (let i = 0; i < data.length; i++) {
    if (data[i] === separator) {
      return [data.slice(0, i), data.slice(i + 1, data.length)];
    }
  }

  return [data, ``];
}

/**
 * Takes a list of elements, and groups them into partitions based on a supplied comparison check with their neighbors
 * i.e. will only group things together if they pass `shouldPartitionWith` and are directly adjacent
 *
 * @param arr - array of any elements
 * @param shouldPartitionWith - takes two adjacent elements from `arr`, and returns true if they should be grouped in the same partition
 *
 * @example
 * groupNeighboringEvents([{ name: "four" }, { name: "test" }, { name: "foo" }, { name: "asdf" }], (a, b) => a.name.length === b.name.length)
 * -> [
 *  [{ name: "four" }, { name: "test" }],
 *  [{ name: "foo" }],
 *  [{ name: "asdf" }]
 * ]
 */
export function groupNeighboringEvents<T>(arr: T[], shouldPartitionWith: (a: T, b: T) => boolean): T[][] {
  if (arr.length === 0) {
    return [];
  }

  let prev = arr[0];
  let currentPartition = [prev];
  const allPartitions = [currentPartition];

  for (let i = 1; i < arr.length; i++) {
    const curr = arr[i];

    if (shouldPartitionWith(curr, prev)) {
      currentPartition.push(curr);
    } else {
      currentPartition = [curr];
      allPartitions.push(currentPartition);
    }

    prev = curr;
  }

  return allPartitions;
}

/**
 * TODO: make this less fragile, along with the whole
 * generatePermalink function
 * Takes a distinct id, and creates an insights bookmark-like blob
 * to feed into a higher level util function that constructs an insights url
 *
 * @param projectId - the project id
 * @param workspaceId - the workspace id
 * @param distinctId - the distinct id
 * @param dateRange - date range object
 * @param includedEvents - list of events when we do "hide all but..."
 * @param excludedEvents - list of events when we hide a few of them
 *
 * @return - a full url to an insights report in the desired project/workspace filtered down to the distinct id
 */
export function getInsightsLinkUrl({
  projectId,
  workspaceId,
  distinctId,
  dateRange,
  includedEvents,
  excludedEvents,
  dataGroupId,
}: {
  projectId: string;
  workspaceId: string;
  distinctId: string;
  dateRange: DateRange;
  includedEvents: string[];
  excludedEvents: string[];
  dataGroupId: string;
}) {
  return generatePermalink({
    projectId,
    workspaceId,
    query: {
      dateRange,
      dataGroupId,
      showClauses: [
        {
          showEvent: {
            event: `$all_events`,
          },
        },
      ],
      groupByProperties: [
        {
          property: {
            type: `STRING`,
            source: `EVENTS`,
            name: `$event_name`,
          },
        },
      ],
      propertyFilters: [
        {
          property: {
            name: `$distinct_id`,
            source: `EVENTS`,
            typecast: `STRING`,
          },
          stringFilter: {
            operator: `EQUALS`,
            operands: [distinctId],
          },
        },
        ...(includedEvents.length > 0
          ? [
              {
                property: {
                  name: `$event_name`,
                  source: `EVENTS`,
                  typecast: `STRING`,
                },
                stringFilter: {
                  operator: `EQUALS`,
                  operands: includedEvents,
                },
              },
            ]
          : []),
        ...(excludedEvents.length > 0
          ? [
              {
                property: {
                  name: `$event_name`,
                  source: `EVENTS`,
                  typecast: `STRING`,
                },
                stringFilter: {
                  operator: `DOES_NOT_EQUAL`,
                  operands: excludedEvents,
                },
              },
            ]
          : []),
      ],
    },
  });
}

export const getProfilePath = (distinctId, dataGroupId, params = {}) => {
  return `${getReportUrl(`profile`, {projectId: getProjectId(), workspaceId: getWorkspaceId()})}#${encodeQueryParams({
    ...(distinctId
      ? {
          distinct_id: distinctId,
        }
      : {}),
    ...(dataGroupId
      ? {
          data_group_id: dataGroupId,
        }
      : {}),
  })}${Object.keys(params).length ? `&` + jsurl.stringify(params) : ``}`;
};

export const extractUserSummaryProperties = (
  userProperties: Map<string, FormattedProperty>,
  extra?: {dataGroupID: string | number; dataGroup: Obj},
): UserSummaryPropertyVariables => {
  if (!userProperties) {
    return {
      avatarUrl: null,
      lastLocation: null,
      name: null,
      email: null,
      lastSeen: null,
    };
  }

  const avatarUrl: string = getRawPropertyValue(userProperties.get(MPProperties.Avatar)) as string;

  let lastLocation;
  if (userProperties.get(MPProperties.CountryCode)) {
    lastLocation = [
      getRenderInputPropertyValue(userProperties.get(MPProperties.City)),
      getRenderInputPropertyValue(userProperties.get(MPProperties.Region)),
      getRenderInputPropertyValue(userProperties.get(MPProperties.CountryCode)) || `Unknown`,
    ]
      .filter(Boolean)
      .join(`, `);
  }

  const nameProperties = getNameProperties(userProperties);
  let name = getUserName(nameProperties, DEFAULT_USER_NAME);
  // If this is a data group and has no set name, override it with the group display name
  if (extra.dataGroupID && name === DEFAULT_USER_NAME) {
    name = `${extra.dataGroup?.displayName || `Group`} Profile`;
  }

  return {
    avatarUrl,
    lastLocation,
    name,
    email: getRenderInputPropertyValue(userProperties.get(MPProperties.Email)),
    lastSeen: getRenderInputPropertyValue(userProperties.get(MPProperties.LastSeen)),
  };
};
